import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import { SR_API_ACTION_UTILITY_PARTNER_PM_SOFTWARE_UPSERT } from 'api/utilityActionTypes';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityPartnerActions from 'client/actions/UtilityPartner';

import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';

import './UpRentManagerUnitTypesPicker.less';

const SAVE_DEBOUNCE_MS = 500;

const UNTYPED_KEY = '__untyped__';
const UNITS_FIRST_PAGE_SIZE = 10;
const UNITS_NEXT_PAGE_SIZE = 20;

// Initial checked state: a saved config wins (checked = not excluded); otherwise everything
// is checked except the globally-non-residential defaults (blockedByDefault), which stay
// re-checkable.
const buildInitialCheckedState = (unitTypes, excludedUnitTypes) => {
  const checkedByName = {};

  unitTypes.forEach((unitType) => {
    if (unitType.name === null) {
      return;
    }

    checkedByName[unitType.name] = Array.isArray(excludedUnitTypes)
      ? !excludedUnitTypes.includes(unitType.name)
      : !unitType.blockedByDefault;
  });

  return checkedByName;
};

const formatMarketRent = (marketRent) => {
  if (marketRent === null || marketRent === undefined) {
    return '—';
  }

  return `$${Number(marketRent).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
};

const formatCount = (count) => Number(count).toLocaleString('en-US');

const UpRentManagerUnitTypesPicker = forwardRef(
  ({ utilityPartner, integration, onSaved, embedded }, ref) => {
    const dispatch = useDispatch();

    const [unitTypes, setUnitTypes] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [checkedByName, setCheckedByName] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [hasSaved, setHasSaved] = useState(false);
    // Address drill-down per housing type, keyed by unit type name (UNTYPED_KEY for the
    // "no housing type" bucket). Collapsing keeps the fetched pages cached so re-expanding
    // is instant.
    const [expandedByKey, setExpandedByKey] = useState({});
    const [unitsByKey, setUnitsByKey] = useState({});

    // Refs mirror the latest data for the debounced/chained save, which runs outside the
    // render cycle (timers, flush on unmount / on onboarding Continue).
    const isMountedRef = useRef(true);
    const unitTypesRef = useRef(null);
    const checkedByNameRef = useRef({});
    const debounceTimerRef = useRef(null);
    // Saves are chained sequentially: the BFF dedupes concurrent dispatches of the same
    // action type (postWithState aborts when one is inProgress), so a second toggle's save
    // must wait for the in-flight one instead of being silently dropped.
    const savePromiseRef = useRef(Promise.resolve());

    const applyCheckedState = (nextCheckedByName) => {
      checkedByNameRef.current = nextCheckedByName;
      setCheckedByName(nextCheckedByName);
    };

    const persistNow = () => {
      const runSave = () =>
        new Promise((resolve) => {
          const namedTypes = (unitTypesRef.current || []).filter(
            (unitType) => unitType.name !== null,
          );
          // Always send the full unchecked list (raw names), including any blockedByDefault
          // ones left unchecked — non-null config is the exact source of truth for sync.
          const excludedUnitTypes = namedTypes
            .filter((unitType) => !checkedByNameRef.current[unitType.name])
            .map((unitType) => unitType.name);

          if (isMountedRef.current) {
            setIsSaving(true);
            setSaveError(null);
          }

          dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_PARTNER_PM_SOFTWARE_UPSERT,
            payload: {
              utilityPartnerId: utilityPartner.id,
              propertyManagementSoftwareId: integration.propertyManagementSoftwareId,
              subdomain: integration.subdomain,
              excludedUnitTypes: excludedUnitTypes,
            },
            onSuccess: (updatedIntegration) => {
              if (isMountedRef.current) {
                setIsSaving(false);
                setHasSaved(true);
                if (onSaved) {
                  onSaved(updatedIntegration);
                }
              }
              resolve();
            },
            onError: (error) => {
              if (isMountedRef.current) {
                setIsSaving(false);
                setSaveError(error.message || 'Failed to save housing types.');
              }
              resolve();
            },
          });
        });

      savePromiseRef.current = savePromiseRef.current.then(runSave);
      return savePromiseRef.current;
    };

    const scheduleSave = () => {
      setHasSaved(false);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        persistNow();
      }, SAVE_DEBOUNCE_MS);
    };

    // Runs any pending debounced save immediately and resolves when every queued save is
    // done. The onboarding Continue flow awaits this so its own upsert can't race (and get
    // deduped against) an auto-save.
    const flushPendingSave = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        return persistNow();
      }
      return savePromiseRef.current;
    };

    useImperativeHandle(ref, () => ({ flushPendingSave }));

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
        // Don't lose the last toggle when navigating away mid-debounce.
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
          persistNow();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadUnitTypes = useCallback(() => {
      setIsLoading(true);
      setLoadError(null);

      dispatch(UtilityPartnerActions.getRentManagerUnitTypes, {
        utilityPartnerId: utilityPartner.id,
        onSuccess: (response) => {
          unitTypesRef.current = response.unitTypes;
          setUnitTypes(response.unitTypes);
          checkedByNameRef.current = buildInitialCheckedState(
            response.unitTypes,
            integration.excludedUnitTypes,
          );
          setCheckedByName(checkedByNameRef.current);
          setIsLoading(false);
        },
        onError: (error) => {
          setLoadError(error.message || 'Failed to load housing types.');
          setIsLoading(false);
        },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [utilityPartner.id, integration.id]);

    useEffect(() => {
      loadUnitTypes();
    }, [loadUnitTypes]);

    const namedUnitTypes = (unitTypes || []).filter((unitType) => unitType.name !== null);
    const untypedUnitType = (unitTypes || []).find((unitType) => unitType.name === null);

    const onToggle = (name) => {
      applyCheckedState({
        ...checkedByNameRef.current,
        [name]: !checkedByNameRef.current[name],
      });
      scheduleSave();
    };

    const onRowKeyDown = (event, name) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        onToggle(name);
      }
    };

    const onSetAll = (isChecked) => {
      const nextState = {};
      namedUnitTypes.forEach((unitType) => {
        nextState[unitType.name] = isChecked;
      });
      applyCheckedState(nextState);
      scheduleSave();
    };

    const loadUnits = (unitsKey, unitTypeName, offset, limit) => {
      setUnitsByKey((current) => ({
        ...current,
        [unitsKey]: {
          units: current[unitsKey]?.units || [],
          total: current[unitsKey]?.total ?? null,
          isLoading: true,
          error: null,
        },
      }));

      dispatch(UtilityPartnerActions.getRentManagerUnitTypeUnits, {
        utilityPartnerId: utilityPartner.id,
        unitTypeName: unitTypeName,
        offset: offset,
        limit: limit,
        onSuccess: (response) => {
          setUnitsByKey((current) => ({
            ...current,
            [unitsKey]: {
              units: [...(current[unitsKey]?.units || []), ...response.units],
              total: response.total,
              isLoading: false,
              error: null,
            },
          }));
        },
        onError: (error) => {
          setUnitsByKey((current) => ({
            ...current,
            [unitsKey]: {
              units: current[unitsKey]?.units || [],
              total: current[unitsKey]?.total ?? null,
              isLoading: false,
              error: error.message || 'Failed to load addresses.',
            },
          }));
        },
      });
    };

    const onToggleExpand = (unitsKey, unitTypeName) => {
      const willExpand = !expandedByKey[unitsKey];
      setExpandedByKey((current) => ({ ...current, [unitsKey]: willExpand }));

      const entry = unitsByKey[unitsKey];
      if (willExpand && !entry?.units?.length && !entry?.isLoading && entry?.total !== 0) {
        loadUnits(unitsKey, unitTypeName, 0, UNITS_FIRST_PAGE_SIZE);
      }
    };

    const onViewMoreUnits = (unitsKey, unitTypeName) => {
      const entry = unitsByKey[unitsKey];
      if (!entry || entry.isLoading) {
        return;
      }
      loadUnits(unitsKey, unitTypeName, entry.units.length, UNITS_NEXT_PAGE_SIZE);
    };

    // The address count + chevron form ONE interactive expand target (wider hit area than
    // the bare chevron); the inner chevron div is purely visual. stopPropagation keeps the
    // surrounding row's checkbox toggle untouched.
    const renderExpandArea = (unitsKey, unitTypeName, label, unitCount) => {
      const isExpanded = !!expandedByKey[unitsKey];

      return (
        <div
          className="upRmUnitTypeExpandArea"
          styleName="rowExpandArea"
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Hide' : 'Show'} addresses for ${label}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpand(unitsKey, unitTypeName);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              onToggleExpand(unitsKey, unitTypeName);
            }
          }}
        >
          <span styleName="rowCount">{pluralize('address', unitCount, true)}</span>
          <div styleName={isExpanded ? 'rowExpandToggle expanded' : 'rowExpandToggle'} />
        </div>
      );
    };

    // Same green spinner as the picker's own initial load, sized down for the muted detail rows.
    const renderUnitsLoading = (message) => (
      <span styleName="unitsLoading">
        <img
          src="/images/green-spinner.svg"
          alt="loading spinner"
          width="14px"
          height="14px"
          styleName="unitsLoadingSpinner"
        />
        <span styleName="unitsMessage">{message}</span>
      </span>
    );

    const renderUnitsPanel = (unitsKey, unitTypeName, knownTotal) => {
      if (!expandedByKey[unitsKey]) {
        return null;
      }

      const entry = unitsByKey[unitsKey];

      if (entry?.error) {
        const onRetry = () =>
          loadUnits(
            unitsKey,
            unitTypeName,
            entry.units.length,
            entry.units.length ? UNITS_NEXT_PAGE_SIZE : UNITS_FIRST_PAGE_SIZE,
          );

        return (
          <div className="upRmUnitTypeUnits" styleName="unitsPanel">
            <span styleName="unitsMessage">{entry.error}</span>
            <span
              className="upRmUnitTypeUnitsRetry"
              styleName="unitsLink"
              role="button"
              tabIndex={0}
              onClick={onRetry}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onRetry();
                }
              }}
            >
              Retry
            </span>
          </div>
        );
      }

      if (!entry || (entry.isLoading && !entry.units.length)) {
        // The row's summary count is known before the first page arrives, so the copy can say
        // exactly what's coming, e.g. "Loading 10 of 2,397 addresses…".
        const firstPageCount = Math.min(UNITS_FIRST_PAGE_SIZE, knownTotal || UNITS_FIRST_PAGE_SIZE);
        const message = knownTotal
          ? `Loading ${formatCount(firstPageCount)} of ${formatCount(knownTotal)} addresses…`
          : 'Loading addresses…';

        return (
          <div className="upRmUnitTypeUnits" styleName="unitsPanel">
            {renderUnitsLoading(message)}
          </div>
        );
      }

      if (entry.total === 0) {
        return (
          <div className="upRmUnitTypeUnits" styleName="unitsPanel">
            <span styleName="unitsMessage">No addresses currently have this housing type.</span>
          </div>
        );
      }

      return (
        <div className="upRmUnitTypeUnits" styleName="unitsPanel">
          {entry.units.map((unit) => (
            <div className="upRmUnitTypeUnitRow" styleName="unitRow" key={unit.unitId}>
              <span styleName="unitAddress">{unit.address || unit.unitName || '—'}</span>
              <span styleName="unitRent">{formatMarketRent(unit.marketRent)}</span>
              <span styleName="unitOccupancy">{unit.occupancyStatus || '—'}</span>
            </div>
          ))}
          {entry.units.length < entry.total &&
            (entry.isLoading ? (
              renderUnitsLoading(
                `Loading ${formatCount(
                  Math.min(UNITS_NEXT_PAGE_SIZE, entry.total - entry.units.length),
                )} more…`,
              )
            ) : (
              <span
                className="upRmUnitTypeUnitsViewMore"
                styleName="unitsLink"
                role="button"
                tabIndex={0}
                onClick={() => onViewMoreUnits(unitsKey, unitTypeName)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onViewMoreUnits(unitsKey, unitTypeName);
                  }
                }}
              >
                {`View more (showing ${formatCount(entry.units.length)} of ${formatCount(
                  entry.total,
                )})`}
              </span>
            ))}
        </div>
      );
    };

    const pickerStyleName = embedded ? 'picker embedded' : 'picker';

    if (isLoading) {
      return (
        <div className="upRentManagerUnitTypesPicker" styleName={pickerStyleName}>
          <div styleName="loadingBox">
            <img
              src="/images/green-spinner.svg"
              alt="loading spinner"
              width="28px"
              height="28px"
              styleName="loadingSpinner"
            />
            <span styleName="loadingText">Loading your housing types from Rent Manager…</span>
          </div>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="upRentManagerUnitTypesPicker" styleName={pickerStyleName}>
          <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED} marginBottom="12px">
            {loadError}
          </SrErrorMessage>
          <SrButton theme={SR_BUTTON_THEME_ADOBE_GREEN} onClick={loadUnitTypes}>
            Retry
          </SrButton>
        </div>
      );
    }

    const checkedCount = namedUnitTypes.filter((unitType) => checkedByName[unitType.name]).length;

    const hintLine = (
      <span styleName="hint">
        Not sure what a housing type covers? Click the arrow on any row to see its addresses,
        rent, and occupancy.
      </span>
    );

    return (
      <div className="upRentManagerUnitTypesPicker" styleName={pickerStyleName}>
        {!embedded && (
          <div styleName="header">
            <span styleName="title">Select the housing types to sync</span>
            <span styleName="subtitle">
              When a new tenant is scheduled to move in, Utility Profit reaches out to them to
              get their utilities set up before move-in day. The housing types below come
              straight from your Rent Manager. We&apos;ll only contact move-ins at the ones you
              check. Unchecked housing types are ignored, and we&apos;ll never message those
              tenants.
            </span>
            {hintLine}
          </div>
        )}
        {/* Embedded: the SrCard header owns the title/subtitle, but the hint still applies */}
        {embedded && <div styleName="header embeddedHeader">{hintLine}</div>}
        <div styleName="toolbar">
          <span styleName="toolbarCount">
            {checkedCount} of {namedUnitTypes.length} housing types will sync
          </span>
          <span styleName="toolbarActions">
            {isSaving && <span styleName="saveStatus">Saving…</span>}
            {!isSaving && hasSaved && (
              <span styleName="saveStatus saved">Saved automatically</span>
            )}
            <span styleName="toolbarLink" onClick={() => onSetAll(true)}>
              Select all
            </span>
            <span styleName="toolbarLink" onClick={() => onSetAll(false)}>
              Deselect all
            </span>
          </span>
        </div>
        <div styleName="list">
          {namedUnitTypes.map((unitType) => (
            <React.Fragment key={unitType.name}>
              <div
                styleName="row"
                role="checkbox"
                aria-checked={!!checkedByName[unitType.name]}
                aria-label={unitType.name}
                tabIndex={0}
                onClick={() => onToggle(unitType.name)}
                onKeyDown={(event) => onRowKeyDown(event, unitType.name)}
              >
                <div styleName="rowCheckboxIcon" />
                <span styleName="rowLabel">{unitType.name}</span>
                {renderExpandArea(unitType.name, unitType.name, unitType.name, unitType.unitCount)}
              </div>
              {renderUnitsPanel(unitType.name, unitType.name, unitType.unitCount)}
            </React.Fragment>
          ))}
          {untypedUnitType && (
            <React.Fragment key={UNTYPED_KEY}>
              <div
                styleName="row untyped"
                role="checkbox"
                aria-checked
                aria-disabled
                aria-label="No housing type set in Rent Manager (always included)"
              >
                <div styleName="rowCheckboxIcon" />
                <span styleName="rowLabel">
                  No housing type set in Rent Manager (always included)
                </span>
                {renderExpandArea(
                  UNTYPED_KEY,
                  undefined,
                  'units with no housing type',
                  untypedUnitType.unitCount,
                )}
              </div>
              {renderUnitsPanel(UNTYPED_KEY, undefined, untypedUnitType.unitCount)}
            </React.Fragment>
          )}
        </div>
        {saveError && (
          <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED} marginBottom="12px">
            {saveError}
          </SrErrorMessage>
        )}
      </div>
    );
  },
);

UpRentManagerUnitTypesPicker.displayName = 'UpRentManagerUnitTypesPicker';

UpRentManagerUnitTypesPicker.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
  integration: PropTypes.object.isRequired,
  onSaved: PropTypes.func,
  // Rendered inside a shared SrCard (settings page): the card provides the chrome and
  // headline, so the picker skips its own container styling and header.
  embedded: PropTypes.bool,
};

UpRentManagerUnitTypesPicker.defaultProps = {
  embedded: false,
};

export default UpRentManagerUnitTypesPicker;
